import {
  Component,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  Event,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';
import { Intercom } from '@intercom/messenger-js-sdk';
import { Subject, takeUntil } from 'rxjs';

import { Types } from '../domain/types.domain';
import { City } from '../domain/city/entities/city.entity';

import { SettingsInstruction } from '../builder/instructions/settings/settings.instruction';
import { SocialLoginProviderInstruction } from '../builder/instructions/providers/social-login-provider.instruction';

import { LoadingSpinnerComponent } from '../@ads/components/loading-spinner';
import { AppHandler } from './app.handler';
import { FaviconService } from './favicon.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, LoadingSpinnerComponent],
  providers: [
    AppHandler,
    new SettingsInstruction(),
    new SocialLoginProviderInstruction(),
  ],
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent {
  private readonly _settings = inject(Types.Settings);
  private readonly _appHandler = inject(AppHandler);
  private readonly _faviconService = inject(FaviconService);

  private readonly _destroy$ = new Subject<void>();

  public loading = signal(false);

  constructor(private router: Router) {
    this.router.events.subscribe((event: Event) => {
      switch (true) {
        case event instanceof NavigationStart: {
          this.loading.set(true);
          break;
        }

        case event instanceof NavigationEnd: {
          this.loading.set(false);
          this._applyCityFavicon();
          break;
        }

        case event instanceof NavigationCancel:
        case event instanceof NavigationError: {
          this.loading.set(false);
          break;
        }
        default: {
          break;
        }
      }
    });
  }

  ngAfterViewInit(): void {
    this._appHandler
      .cityChange()
      .pipe(takeUntil(this._destroy$))
      .subscribe((city) => {
        if (!city) {
          return;
        }

        if (city.useIntercom) {
          this._setupIntercomIntegration();
        }
      });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  /**
   * APR-3347 — o favicon acompanha a cidade da rota. A fonte é o `route.data`
   * preenchido pelo cityResolver, e não o AppHandler: o resolver escreve na
   * instância provida pela rota, enquanto este componente provê a sua própria
   * (app.routes.ts e o decorator abaixo registram `AppHandler` em injetores
   * diferentes). Rota sem cidade — seleção, pylon, sso — volta ao ícone padrão.
   */
  private _applyCityFavicon(): void {
    this._faviconService.update(this._cityFromRoute()?.faviconUrl);
  }

  private _cityFromRoute(): City | null {
    let route = this.router.routerState.snapshot.root;

    while (route.firstChild) {
      route = route.firstChild;
    }

    return route.data['city'] ?? null;
  }

  private _setupIntercomIntegration(): void {
    const identity = this._appHandler.getCity()?.identidade;
    const city = identity?.slice(0, -2);
    const state = identity?.slice(-2);

    Intercom({
      app_id: this._settings.intercom.appId,
      stage: this._settings.stage,
      city,
      state,
    });
  }
}
